import { NavLink, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import {
  LayoutDashboard, Image as ImageIcon, Settings,
  Home, Users, Newspaper, Boxes, Tag, Briefcase, Store, Plus, CalendarDays, MessageSquareQuote, Mail, Bot, ChevronDown, Globe, Radar, CreditCard, ReceiptText, ServerCog, Wallet, ClipboardList, ListChecks, Lock, TrendingUp, LifeBuoy, type LucideIcon,
} from 'lucide-react';
import { hasPanelCapability, type PanelRole } from '@palacete/shared/permissions';
import { api, capabilitiesCached, type PanelCapabilities } from '../../lib/api';
import type { CollectionTypeDef } from '../../lib/types';
import { pickLoc } from '../../lib/collections';
import AddContentModal from '../forms/AddContentModal';
import { coreNavItems, platformPluginsNav, type PanelNavItem } from '../../lib/panelNavigation';
import { useChecklist } from '../../lib/useChecklist';
import { useSubmissionsUnread } from '../../lib/useSubmissionsUnread';
import { useHelp } from '../../lib/useHelp';
import BrandWordmark from './BrandWordmark';

// Iconos por nombre (lucide) con fallback por arquetipo y, en último caso, Boxes.
const ICON_BY_NAME: Record<string, LucideIcon> = {
  home: Home, users: Users, newspaper: Newspaper, tag: Tag,
  briefcase: Briefcase, store: Store, boxes: Boxes,
};
const ICON_BY_ARCHETYPE: Record<string, LucideIcon> = {
  listing: Home, people: Users, editorial: Newspaper,
  catalog: Store, social: Tag, ops: Briefcase,
};
function iconFor(t: CollectionTypeDef): LucideIcon {
  return (t.icon && ICON_BY_NAME[t.icon]) || ICON_BY_ARCHETYPE[t.archetype] || Boxes;
}

interface TenantSummary { id: string; name: string; slug: string; domain: string | null }

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  isSaasAdmin?: boolean;
  role: PanelRole;
  tenants?: TenantSummary[];
  activeTenantId?: string | null;
  activeTenantName?: string | null;
  onTenantSwitch?: (id: string) => void;
}

export default function Sidebar({
  mobileOpen = false,
  onMobileClose,
  isSaasAdmin = false,
  role,
  tenants = [],
  activeTenantId = null,
  activeTenantName = null,
  onTenantSwitch,
}: SidebarProps) {
  const navigate = useNavigate();
  const [types, setTypes] = useState<CollectionTypeDef[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [caps, setCaps] = useState<PanelCapabilities | null>(null);
  const { pendingCount: checklistPending } = useChecklist();
  const { unreadCount: messagesUnread } = useSubmissionsUnread();
  const { openHelp } = useHelp();

  const loadTypes = useCallback(() => {
    return api.collectionTypes.list()
      .then((t) => setTypes([...t].sort((a, b) => a.sortOrder - b.sortOrder)))
      .catch(() => { /* sin colecciones aún */ });
  }, []);

  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => { capabilitiesCached().then(setCaps).catch(() => setCaps(null)); }, [activeTenantId]);

  // BIZ-07A: por madurez el item no existe en la UI (ni teaser); por plan o por
  // acceso vencido se muestra con candado y manda a "Mi pago".
  //
  // Quién decide es `stages`, NO `planFeatures`: este último llega vacío cuando el
  // acceso venció, y leerlo hacía desaparecer Reservas y Asistente del menú de
  // quien solo debía pagar — justo el caso donde hace falta el camino al pago.
  // Con la etapa: si no es 'ga' la feature aún no existe para clientes (esconder);
  // si es 'ga' y aun así no la tiene, es cosa de dinero (candado).
  function featureGate(item: PanelNavItem): 'visible' | 'locked' | 'hidden' {
    if (!item.feature || !caps) return 'visible';
    if (caps.features[item.feature]) return 'visible';
    return (caps.stages[item.feature] ?? 'ga') === 'ga' ? 'locked' : 'hidden';
  }

  function lockedHint(): string {
    return caps && !caps.billingActive
      ? 'Tu acceso venció. Reactívalo para volver a usarlo.'
      : 'Disponible en un plan superior';
  }

  function isBeta(item: PanelNavItem): boolean {
    return Boolean(item.feature && caps?.stages[item.feature] && caps.stages[item.feature] !== 'ga');
  }

  async function handleAdded(createdKeys: string[]) {
    setAddOpen(false);
    await loadTypes();
    if (createdKeys[0]) navigate(`/c/${createdKeys[0]}`);
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
      isActive
        ? 'bg-ui-accent/15 text-ui-text border border-ui-accent/30'
        : 'text-ui-text-muted hover:text-ui-text hover:bg-ui-text/[0.06]'
    }`;

  function renderNavItem(item: PanelNavItem, Icon: LucideIcon, end = false, badge = 0) {
    const gate = featureGate(item);
    if (gate === 'locked') {
      return (
        <button
          key={item.id}
          type="button"
          onClick={() => navigate('/billing')}
          className={`${linkClass({ isActive: false })} w-full text-left`}
          title={lockedHint()}
        >
          <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
          {item.label}
          <Lock size={13} className="ml-auto flex-shrink-0 text-ui-text-subtle" />
        </button>
      );
    }
    return (
      <NavLink key={item.id} to={item.to} end={end} className={linkClass}>
        <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
        {item.label}
        {badge > 0 && (
          <span className="ml-auto text-xs font-semibold leading-none bg-ui-accent text-ui-bg rounded-full px-1.5 py-1 min-w-[18px] text-center">
            {badge}
          </span>
        )}
        {isBeta(item) && (
          <span className="ml-auto text-sm font-medium leading-none text-ui-accent bg-ui-accent/10 rounded px-1.5 py-0.5">
            Beta
          </span>
        )}
      </NavLink>
    );
  }

  const canUseAssistant = hasPanelCapability(role, 'assistant.view');
  const canUseSettings = hasPanelCapability(role, 'settings.view');
  const canViewBilling = hasPanelCapability(role, 'billing.view');
  const canUseSurveys = hasPanelCapability(role, 'marketing.surveys.view');

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-dropdown bg-black/60 md:hidden"
          onClick={onMobileClose}
        />
      )}
    <aside className={[
      'w-56 bg-ui-surface border-r border-ui-border flex flex-col',
      'md:relative md:shrink-0 md:translate-x-0 md:inset-auto md:z-auto',
      'fixed inset-y-0 left-0 z-drawer transition-transform duration-200',
      mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
    ].join(' ')}>
      <div className="flex flex-col items-start gap-2.5 px-3 pt-4 pb-3 border-b border-ui-border">
        <span className="flex items-center min-w-0 text-ui-text">
          <BrandWordmark className="text-3xl" />
        </span>
        <span className="max-w-full truncate text-sm font-medium text-ui-accent bg-ui-accent/10 px-2 py-0.5 rounded">
          {activeTenantName || 'Sitio'}
        </span>
      </div>

      {/* Tenant switcher — solo para saas_admin con más de un tenant */}
      {isSaasAdmin && tenants.length > 1 && (
        <div className="px-3 py-2 border-b border-ui-border">
          <div className="relative">
            <select
              value={activeTenantId ?? ''}
              onChange={(e) => onTenantSwitch?.(e.target.value)}
              className="w-full appearance-none rounded-md border border-ui-border bg-ui-bg px-3 py-1.5 pr-7 text-sm text-ui-text focus:outline-none focus:border-ui-accent cursor-pointer"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.domain ? ` · ${t.domain}` : ''}
                </option>
              ))}
            </select>
            <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ui-text-subtle" />
          </div>
        </div>
      )}

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" onClick={onMobileClose}>
        {coreNavItems
          .filter((item) => {
            if (!['dashboard', 'site', 'bookings', 'chatbot'].includes(item.id)) return false;
            if (item.id === 'chatbot' && !canUseAssistant) return false;
            return featureGate(item) !== 'hidden';
          })
          .map((item) => {
            const Icon =
              item.id === 'dashboard' ? LayoutDashboard :
              item.id === 'site' ? Globe :
              item.id === 'bookings' ? CalendarDays :
              Bot;

            return renderNavItem(item, Icon, item.id === 'dashboard');
          })}

        {checklistPending > 0 && (
          <NavLink to="/getting-started" className={linkClass}>
            <span className="w-4 flex-shrink-0"><ListChecks size={15} /></span>
            Primeros pasos
            <span className="ml-auto text-xs font-semibold leading-none bg-ui-accent text-ui-bg rounded-full px-1.5 py-1 min-w-[18px] text-center">
              {checklistPending}
            </span>
          </NavLink>
        )}

        <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-ui-text-subtle">
          Tu presencia online
        </p>
        {coreNavItems
          .filter((item) => ['visibility', 'reputation', 'surveys', 'messages', 'contacts', 'tools'].includes(item.id))
          .filter((item) => item.id !== 'surveys' || canUseSurveys)
          .filter((item) => item.id !== 'tools' || hasPanelCapability(role, 'plugins.view'))
          .filter((item) => featureGate(item) !== 'hidden')
          .map((item) => {
            const Icon =
              item.id === 'tools' ? Boxes :
              item.id === 'visibility' ? Radar :
              item.id === 'reputation' ? MessageSquareQuote :
              item.id === 'surveys' ? ClipboardList :
              item.id === 'messages' ? Mail :
              Users;
            return renderNavItem(item, Icon, false, item.id === 'messages' ? messagesUnread : 0);
          })}

        {/* Colecciones dinámicas (motor genérico) */}
        {types.length > 0 && (
          <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-ui-text-subtle">
            Secciones de contenido
          </p>
        )}
        {types.map((t) => {
          const Icon = iconFor(t);
          const label = pickLoc(t.labels.plural, 'es') || t.key;
          return (
            <NavLink key={t.id} to={`/c/${t.key}`} className={linkClass}>
              <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
              {label}
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
        >
          <span className="w-4 flex-shrink-0"><Plus size={15} /></span>
          Añadir sección
        </button>

        <div className="pt-4 space-y-0.5">
          {coreNavItems
            .filter((item) => ['media'].includes(item.id))
            .map((item) => {
              const Icon = ImageIcon;
              return (
                <NavLink key={item.id} to={item.to} className={linkClass}>
                  <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
                  {item.label}
                </NavLink>
              );
            })}
          {coreNavItems
            .filter((item) => item.id === 'settings' && canUseSettings)
            .map((item) => {
              const Icon = Settings;
              return (
                <NavLink key={item.id} to={item.to} className={linkClass}>
                  <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
                  {item.label}
                </NavLink>
              );
            })}
          {/* "Mi pago": suscripción del propio tenant (≠ "Cobros y planes" del saas_admin). */}
          {coreNavItems
            .filter((item) => item.id === 'billing' && canViewBilling)
            .map((item) => (
              <NavLink key={item.id} to={item.to} className={linkClass}>
                <span className="w-4 flex-shrink-0"><Wallet size={15} /></span>
                {item.label}
              </NavLink>
            ))}
        </div>

        {/* Acceso del dueño del SaaS: cobros y planes. Solo saas_admin; el backend lo verifica. */}
        {isSaasAdmin && (
          <div className="mt-4 pt-4 border-t border-ui-border">
            <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-ui-text-subtle">
              Administración
            </p>
            <NavLink to="/platform/billing" className={linkClass}>
              <span className="w-4 flex-shrink-0"><CreditCard size={15} /></span>
              Cobros y planes
            </NavLink>
            <NavLink to={platformPluginsNav.to} className={linkClass}>
              <span className="w-4 flex-shrink-0"><Boxes size={15} /></span>
              {platformPluginsNav.label}
            </NavLink>
            <NavLink to="/platform/infrastructure" className={linkClass}>
              <span className="w-4 flex-shrink-0"><ServerCog size={15} /></span>
              Infraestructura
            </NavLink>
            <NavLink to="/platform/fiscal" className={linkClass}>
              <span className="w-4 flex-shrink-0"><ReceiptText size={15} /></span>
              Cumplimiento fiscal
            </NavLink>
            <NavLink to="/platform/costs" className={linkClass}>
              <span className="w-4 flex-shrink-0"><TrendingUp size={15} /></span>
              Costo de operación
            </NavLink>
          </div>
        )}
      </nav>

      {/* Pie fijo: la ayuda tiene que estar donde la busca quien está atorado, sin
          depender de cuánto haya scrolleado el menú. */}
      <div className="shrink-0 border-t border-ui-border px-3 py-2">
        <button
          type="button"
          onClick={() => { onMobileClose?.(); openHelp(); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-ui-text-muted hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
        >
          <span className="w-4 flex-shrink-0"><LifeBuoy size={15} /></span>
          Ayuda
        </button>
      </div>

      {addOpen && (
        <AddContentModal
          existingKeys={types.map((t) => t.key)}
          onClose={() => setAddOpen(false)}
          onAdded={handleAdded}
        />
      )}
    </aside>
    </>
  );
}
